import React, { Component, ComponentType, MouseEvent, Ref } from 'react';
import { differenceInMinutes } from 'date-fns';
import OrderStatus from '../OrderStatus/OrderStatus';
import { Avatar, Col, Row, Typography } from 'antd';
import { useTranslation, WithTranslation, withTranslation } from 'react-i18next';
import { Delivery, DeliveryStatus } from '../../api/models/deliveries';
import { DeliveryTracking } from '../../api/models/deliveriesTracking';
import classnames from 'classnames';
import { canBeCanceled, getDirectionType } from '../../utils/deliveries';
import { Button } from '../Button/Button';
import CancelDeliveryButton from '../CancelDeliveryButton/CancelDeliveryButton';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import isEqual from 'react-fast-compare';
import { formatDate } from '../../utils/dates';
import Paper from '../Paper/Paper';
import Map from '../Map';
import styles from './DetailedDeliveryCard.module.less';
import { isMobile, isTablet } from '../../utils/browser';
import VehicleDetails from '../VehicleDetails/VehicleDetails';
import { MapUseCase } from '../Map/Map';

type GetDriverStatusProps = {
  isCNF: Delivery['isCNF'];
  status: Delivery['status'];
  trackingData?: DeliveryTracking;
};

const GetDriverStatus = (props: GetDriverStatusProps) => {
  const { trackingData: { eta } = {}, isCNF, status } = props;
  const { t } = useTranslation();
  let text;
  if (status === DeliveryStatus.Cancelled && isCNF) {
    text = t('deliveries.deliveryStatus.captainNotFound');
  } else {
    if (eta && eta > 0 && (status === DeliveryStatus.Assigned || status === DeliveryStatus.InProgress)) {
      text = t(`activeDeliveriesPage.driverStatus.${status}Eta`, { eta: differenceInMinutes(eta * 1000, 0) });
    } else {
      text = t(`activeDeliveriesPage.driverStatus.${status}`);
    }
  }
  return <Typography.Text className={styles[status]}>{text}</Typography.Text>;
};

type DeliveryCardProps = Delivery & {
  dateFormat?: string;
  isExpanded?: boolean;
  trackingData?: DeliveryTracking;
  onRecreate?: (bookingId: string) => Promise<any>;
  onCancel?: (bookingId: string, reason: string, customReason?: string) => Promise<any>;
  handleExpandClick: (bookingId: string) => void;
  onCancelAttempt?: (bookingId: string) => Promise<any>;
  onCancelAbort?: (bookingId: string) => Promise<any>;
  forwardedRef?: Ref<any>;
} & WithTranslation;

type DeliveryCardState = { isLoading: boolean; mapHeight: number; isMapVisible: boolean };

class DeliveryCard extends Component<DeliveryCardProps, DeliveryCardState> {
  static defaultProps = {
    dateFormat: 'p'
  };

  detailsRef: React.RefObject<HTMLDivElement>;

  constructor(props: DeliveryCardProps) {
    super(props);

    this.state = {
      isLoading: false,
      mapHeight: 0,
      isMapVisible: false
    };

    this.detailsRef = React.createRef();
  }

  shouldComponentUpdate(prevProps: DeliveryCardProps, prevState: DeliveryCardState) {
    return !isEqual(this.state, prevState) || !isEqual(this.props, prevProps);
  }

  componentDidUpdate(prevProps: Readonly<DeliveryCardProps>) {
    if (this.props.isExpanded && !prevProps.isExpanded && this.detailsRef?.current?.clientHeight) {
      this.setState({
        mapHeight: this.detailsRef.current.clientHeight
      });
    }
  }

  componentDidMount() {
    if (this.props.isExpanded && this.detailsRef?.current?.clientHeight) {
      this.setState({
        mapHeight: this.detailsRef.current.clientHeight
      });
    }
  }

  toggleMapVisibility = () => {
    this.setState({ isMapVisible: !this.state.isMapVisible });
  };

  renderDriverDetails(includeMap: boolean) {
    const { id, status, t, pickupLocation, dropOffLocation, driverDetails, trackingData, vehicle } = this.props;
    const { isMapVisible } = this.state;
    return (
      <div
        className={classnames(styles.driverDetailsContainer, {
          [styles.greyBackground]: Boolean(driverDetails),
          [styles.noPadding]: !driverDetails
        })}
      >
        {driverDetails && (
          <div className={styles.driverDetails}>
            <div className={styles.driverAvatar}>
              <Avatar src={driverDetails.driverPicture} />
            </div>
            <div className={styles.driverData}>
              <div className={styles.flexSpaceBetween}>
                <Typography.Text strong>{t('activeDeliveriesPage.yourDriver')}</Typography.Text>
                {vehicle && <VehicleDetails vehicleModel={vehicle.vehicleModel} plateNumber={vehicle.plateNumber} />}
              </div>
              <div className={styles.flexSpaceBetween}>
                <Typography.Text>{driverDetails.driverName}</Typography.Text>
                <Typography.Text>
                  {driverDetails.driverNumber && (
                    <a href={`tel:${driverDetails.driverNumber}`}>{driverDetails.driverNumber}</a>
                  )}
                </Typography.Text>
              </div>
            </div>
          </div>
        )}
        {includeMap && (
          <>
            {isMapVisible && (
              <div className={styles.mapContainer}>
                <Map
                  mapUseCase={MapUseCase.ViewActiveDelivery}
                  pickup={pickupLocation}
                  dropOff={dropOffLocation}
                  driver={trackingData?.driverLocationDetails && { location: trackingData.driverLocationDetails }}
                  directionType={getDirectionType(status)}
                  bookingId={id}
                />
              </div>
            )}
            <div className={styles.mapButtonContainer}>
              <Button block onClick={this.toggleMapVisibility}>
                {isMapVisible
                  ? t('activeDeliveriesPage.hideDriverLocation')
                  : t('activeDeliveriesPage.showDriverLocation')}
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  canBeCanceled() {
    return canBeCanceled(this.props.status);
  }

  onRecreateDelivery = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    this.setState({ isLoading: true });

    this.props.onRecreate!(this.props.bookingId).finally(() => {
      this.setState({ isLoading: false });
    });
  };

  onCancel = (reason: string, customReason?: string) => {
    return this.props.onCancel!(this.props.bookingId, reason, customReason);
  };

  onCancelAttempt = () => {
    return this.props.onCancelAttempt!(this.props.bookingId);
  };

  onCancelAbort = () => {
    return this.props.onCancelAbort!(this.props.bookingId);
  };

  renderActionButton() {
    const { isLoading } = this.state;
    const { isCNF, t, onRecreate, onCancel } = this.props;
    const isCancelable = this.canBeCanceled();
    if ((!isCNF && !isCancelable) || (!onCancel && !onRecreate)) {
      return '';
    }
    return (
      <div className={styles.actionButtonContainer}>
        {isCancelable ? (
          <CancelDeliveryButton
            btnType="default"
            onCancel={this.onCancel}
            onCancelAbort={this.onCancelAbort}
            onCancelAttempt={this.onCancelAttempt}
          />
        ) : (
          <Button
            type="default"
            block
            disabled={isLoading}
            onClick={this.onRecreateDelivery}
            data-testid="recreateButton"
          >
            {t('deliveries.createDeliveryAgainCTA')}
          </Button>
        )}
      </div>
    );
  }

  handleExpandClick = () => {
    const id = this.props.isExpanded ? '' : this.props.id;
    this.props?.handleExpandClick(id.toString());
  };

  renderMobileHeader = () => {
    const { id, status, pickUpTime, dateFormat, isCNF, t, trackingData, isExpanded, orderId } = this.props;
    return (
      <div>
        <div className={classnames(styles.flexSpaceBetween, styles.marginBottom16)}>
          <div>
            <Typography.Text strong>{t('deliveries.deliveryWithId', { id })}</Typography.Text>
          </div>
          <div>
            <OrderStatus status={status} className={styles.orderStatus} />
          </div>
        </div>
        <div className={classnames(styles.flexSpaceBetween, styles.marginBottom16)}>
          <div>
            <GetDriverStatus isCNF={isCNF} status={status} trackingData={trackingData} />
          </div>
          <div>{orderId && <Typography.Text className={styles.lightGreyText}>{orderId}</Typography.Text>}</div>
        </div>
        <div className={styles.flexSpaceBetween}>
          <div>
            {pickUpTime && (
              <Typography.Text className={styles.lightGreyText}>{formatDate(pickUpTime, dateFormat!)}</Typography.Text>
            )}
          </div>
          <div>
            {isExpanded ? (
              <UpOutlined data-testid="expandBtn" className={styles.lightGreyText} onClick={this.handleExpandClick} />
            ) : (
              <DownOutlined data-testid="expandBtn" className={styles.lightGreyText} onClick={this.handleExpandClick} />
            )}
          </div>
        </div>
      </div>
    );
  };

  renderSingleLineHeader = () => {
    const { id, status, pickUpTime, dateFormat, isCNF, t, trackingData, isExpanded, orderId } = this.props;
    return (
      <Row>
        <Col xs={7} lg={6} xl={5}>
          <Typography.Text strong>{t('deliveries.deliveryWithId', { id })}</Typography.Text>
        </Col>
        <Col xs={4} lg={5} xl={5}>
          <Typography.Text className={styles.lightGreyText}>
            {orderId ? t('activeDeliveriesPage.orderId', { orderId }) : '-'}
          </Typography.Text>
        </Col>
        <Col xs={3}>
          {pickUpTime && (
            <Typography.Text className={styles.lightGreyText}>{formatDate(pickUpTime, dateFormat!)}</Typography.Text>
          )}
        </Col>
        <Col xs={6}>
          <GetDriverStatus isCNF={isCNF} status={status} trackingData={trackingData} />
        </Col>
        <Col xs={4} xl={3}>
          <div className={styles.statusContainer}>
            <div>
              <OrderStatus status={status} className={styles.orderStatus} />
            </div>
          </div>
        </Col>
        <Col xs={2} xl={2}>
          <div className={styles.arrow}>
            {isExpanded ? (
              <UpOutlined data-testid="expandBtn" className={styles.lightGreyText} onClick={this.handleExpandClick} />
            ) : (
              <DownOutlined data-testid="expandBtn" className={styles.lightGreyText} onClick={this.handleExpandClick} />
            )}
          </div>
        </Col>
      </Row>
    );
  };

  renderHeader = () => {
    const isMobileDevice = isMobile();

    return isMobileDevice ? this.renderMobileHeader() : this.renderSingleLineHeader();
  };

  render() {
    const {
      id,
      status,
      t,
      pickupLocation,
      dropOffLocation,
      driverNotes,
      passenger,
      trackingData,
      isExpanded,
      forwardedRef,
      cashOnDelivery
    } = this.props;
    const { mapHeight } = this.state;
    const isMapInDriverDetails = isMobile() || isTablet();
    return (
      <Paper className={classnames(styles.card, styles[status])} ref={forwardedRef} data-testid="detailedDeliveryCard">
        {this.renderHeader()}
        {isExpanded && (
          <div className={styles.detailsContainer}>
            <div className={styles.details} ref={this.detailsRef}>
              {this.renderDriverDetails(isMapInDriverDetails)}
              <div className={styles.deliveryDetails}>
                <div className={styles.addressTitleContainer}>
                  <div className={styles.pin} />
                  <div className={styles.addressTitle}>
                    <Typography.Text className={styles.largeText} strong>
                      {t('general.pickup')}
                    </Typography.Text>
                  </div>
                </div>
                <div className={classnames(styles.pickupDescription, styles.blocked)}>
                  <div className={styles.flexSpaceBetween}>
                    <Typography.Text>{pickupLocation?.building}</Typography.Text>
                  </div>
                  <div>
                    <Typography.Text className={styles.lightGreyText}>{pickupLocation?.name}</Typography.Text>
                  </div>
                </div>
                <div className={styles.addressTitleContainer}>
                  <div className={classnames(styles.pin, styles.dropOffPin)} />
                  <div className={styles.addressTitle}>
                    <Typography.Text className={styles.largeText} strong>
                      {t('general.dropOff')}
                    </Typography.Text>
                  </div>
                </div>
                {passenger && (
                  <div className={styles.flexSpaceBetween}>
                    <Typography.Text>
                      {passenger.firstName} {passenger.lastName}
                    </Typography.Text>
                    <Typography.Text>
                      {passenger.phoneNumber && <a href={`tel:+${passenger.phoneNumber}`}>+{passenger.phoneNumber}</a>}
                    </Typography.Text>
                  </div>
                )}
                <div>
                  <Typography.Text className={classnames(styles.lightGreyText, styles.blocked)}>
                    {dropOffLocation?.location ? dropOffLocation.name : t('deliveries.dropoffLocation.noCoordinates')}
                  </Typography.Text>
                </div>
                {driverNotes && (
                  <div className={styles.driverNotes}>
                    <div>
                      <Typography.Text className={styles.largeText} strong>
                        {t('deliveries.deliveryDetails.text')}
                      </Typography.Text>
                    </div>
                    <Typography.Text>{driverNotes}</Typography.Text>
                  </div>
                )}
                {cashOnDelivery && (
                  <div className={styles.cashOnDelivery}>
                    <div>
                      <Typography.Text className={styles.largeText} strong>
                        {t('deliveries.cashOnDelivery.toBeCollected')}
                      </Typography.Text>
                    </div>
                    <Typography.Text>{cashOnDelivery}</Typography.Text>
                  </div>
                )}
              </div>

              {this.renderActionButton()}
            </div>
            {!isMapInDriverDetails && (
              <div className={styles.mapContainer} style={{ height: mapHeight }}>
                <Map
                  mapUseCase={MapUseCase.ViewActiveDelivery}
                  pickup={pickupLocation}
                  dropOff={dropOffLocation}
                  driver={trackingData?.driverLocationDetails && { location: trackingData.driverLocationDetails }}
                  directionType={getDirectionType(status)}
                  bookingId={id}
                />
              </div>
            )}
          </div>
        )}
      </Paper>
    );
  }
}

export default withTranslation()<ComponentType<DeliveryCardProps>>(DeliveryCard);
